import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../../layouts/PageTitle";
import { useGetAllStaffQuery } from "../../../../store/appApi";
import {
  selectUser,
  selectUserModules,
  selectUserPagePermissions,
} from "../../../../store/authSlice";
import {
  addUntraceableRemarkApi,
  closeUntraceableCaseApi,
  createUntraceableCaseApi,
  deleteUntraceableCaseApi,
  getUntraceableCasesApi,
  reopenUntraceableCaseApi,
  updateUntraceableCaseApi,
} from "../../../../services/untraceableCases";
import { formatDateLabel, toDateInputValue } from "../modemRecovery/config";

const PAGE_KEY = "workforce.modemRecovery";
const LEVELS = { none: 0, read: 1, "read-write": 2 };

const panelClass =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
const inputClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 disabled:bg-gray-50 disabled:text-gray-500";
const labelClass =
  "mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500";

const sortRemarks = (c) =>
  (Array.isArray(c?.remarks) ? [...c.remarks] : []).sort((a, b) =>
    String(b.date).localeCompare(String(a.date)),
  );

const UntraceableList = () => {
  const me = useSelector(selectUser);
  const modules = useSelector(selectUserModules);
  const pagePermissions = useSelector(selectUserPagePermissions);
  const isSuperAdmin = me?.role === "super-admin";

  const explicitLevel = pagePermissions?.[PAGE_KEY];
  const effectiveLevel =
    explicitLevel && explicitLevel !== "inherit"
      ? explicitLevel
      : (modules?.workforce ?? "none");
  const canView = isSuperAdmin || (LEVELS[effectiveLevel] ?? 0) >= LEVELS.read;
  const canWrite =
    isSuperAdmin || (LEVELS[effectiveLevel] ?? 0) >= LEVELS["read-write"];

  const { data: staffData } = useGetAllStaffQuery(
    { page: 1, limit: 500 },
    { skip: !isSuperAdmin },
  );
  const allStaff = useMemo(
    () =>
      (Array.isArray(staffData?.staff) ? staffData.staff : [])
        .filter((s) => s?.status !== false)
        .sort((a, b) => String(a.name).localeCompare(String(b.name))),
    [staffData],
  );
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const employeeId = isSuperAdmin ? selectedEmployeeId : "";
  const viewingLabel = isSuperAdmin
    ? allStaff.find((s) => String(s._id) === String(selectedEmployeeId))?.name
    : me?.name;

  const [statusFilter, setStatusFilter] = useState("open"); // open | closed | all
  const [cases, setCases] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const canLoad = canView && (!isSuperAdmin || Boolean(employeeId));

  const reload = () => {
    if (!canLoad) {
      setCases([]);
      setSummary(null);
      return;
    }
    setLoading(true);
    getUntraceableCasesApi({
      employeeId: employeeId || undefined,
      status: statusFilter === "all" ? undefined : statusFilter,
    })
      .then((res) => {
        setCases(res?.data?.cases || []);
        setSummary(res?.data?.summary || null);
      })
      .catch(() => {
        setCases([]);
        setSummary(null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(reload, [canLoad, employeeId, statusFilter]);

  // ── local UI state ──────────────────────────────────────────────────────
  const [addOpen, setAddOpen] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [draftPhone, setDraftPhone] = useState("");
  const [saving, setSaving] = useState(false);

  const [remarkDrafts, setRemarkDrafts] = useState({}); // { [caseId]: { text, date } }
  const [busyId, setBusyId] = useState("");
  const [historyCase, setHistoryCase] = useState(null);
  const [editingId, setEditingId] = useState("");
  const [editDraft, setEditDraft] = useState({ customerName: "", phone: "" });

  const getRemarkDraft = (id) =>
    remarkDrafts[id] || { text: "", date: toDateInputValue() };
  const setRemarkDraft = (id, patch) =>
    setRemarkDrafts((prev) => ({
      ...prev,
      [id]: { ...getRemarkDraft(id), ...patch },
    }));

  const withEmployee = (payload = {}) =>
    employeeId ? { ...payload, employeeId } : payload;

  const errText = (error, fallback) =>
    error?.response?.data?.message || fallback;

  const handleAdd = async () => {
    const customerName = draftName.trim();
    const phone = draftPhone.trim();
    if (!customerName && !phone) return;
    try {
      setSaving(true);
      await createUntraceableCaseApi(withEmployee({ customerName, phone }));
      setAddOpen(false);
      setDraftName("");
      setDraftPhone("");
      reload();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Could not add case",
        text: errText(error, "The case could not be created."),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddRemark = async (c) => {
    const draft = getRemarkDraft(c._id);
    const text = String(draft.text || "").trim();
    if (!text) return;
    try {
      setBusyId(c._id);
      await addUntraceableRemarkApi(
        c._id,
        withEmployee({ text, date: draft.date || toDateInputValue() }),
      );
      setRemarkDrafts((prev) => ({
        ...prev,
        [c._id]: { text: "", date: toDateInputValue() },
      }));
      reload();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Could not add follow-up",
        text: errText(error, "The remark could not be saved."),
      });
    } finally {
      setBusyId("");
    }
  };

  const handleClose = async (c) => {
    const { value: note, isConfirmed } = await Swal.fire({
      icon: "question",
      title: "Close this case?",
      input: "text",
      inputLabel: "Outcome / note (optional)",
      inputPlaceholder: "Traced & recovered / written off …",
      showCancelButton: true,
      confirmButtonText: "Close case",
      confirmButtonColor: "#059669",
    });
    if (!isConfirmed) return;
    try {
      setBusyId(c._id);
      await closeUntraceableCaseApi(c._id, withEmployee({ note: note || "" }));
      reload();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Could not close",
        text: errText(error, "The case could not be closed."),
      });
    } finally {
      setBusyId("");
    }
  };

  const handleReopen = async (c) => {
    try {
      setBusyId(c._id);
      await reopenUntraceableCaseApi(c._id, withEmployee());
      reload();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Could not reopen",
        text: errText(error, "The case could not be reopened."),
      });
    } finally {
      setBusyId("");
    }
  };

  const handleDelete = async (c) => {
    const { isConfirmed } = await Swal.fire({
      icon: "warning",
      title: "Delete this case?",
      text: "The customer and its full follow-up trail are removed.",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc2626",
    });
    if (!isConfirmed) return;
    try {
      setBusyId(c._id);
      await deleteUntraceableCaseApi(c._id, employeeId || undefined);
      reload();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Could not delete",
        text: errText(error, "The case could not be removed."),
      });
    } finally {
      setBusyId("");
    }
  };

  const startEdit = (c) => {
    setEditingId(c._id);
    setEditDraft({ customerName: c.customerName || "", phone: c.phone || "" });
  };
  const saveEdit = async (c) => {
    try {
      setBusyId(c._id);
      await updateUntraceableCaseApi(c._id, withEmployee(editDraft));
      setEditingId("");
      reload();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Could not save",
        text: errText(error, "The case could not be updated."),
      });
    } finally {
      setBusyId("");
    }
  };

  const openCount = summary?.open ?? 0;
  const closedCount = summary?.closed ?? 0;
  const ledger = summary?.ledgerUntraceable ?? 0;
  const unlogged = summary?.unlogged ?? 0;

  return (
    <>
      <PageTitle activeMenu="Untraceable" motherMenu="Netplus" />
      <div className="space-y-6">
        <section className={`${panelClass} p-6`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
                Netplus · Modem Recovery
              </div>
              <h1 className="mt-3 text-2xl font-semibold text-gray-900">
                Untraceable Cases
              </h1>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                The named counterpart to the Modem Recovery ledger's{" "}
                <strong>Untraceable</strong> tally. Add a case with the
                customer's name &amp; phone, then log a dated follow-up each time
                it's chased — the trail stays until the case is closed.
              </p>
            </div>
            {isSuperAdmin && (
              <div className="w-64">
                <label className={labelClass}>Employee</label>
                <select
                  className={inputClass}
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                >
                  <option value="">Select an employee…</option>
                  {allStaff.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name} · {s.role}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </section>

        {!canView ? (
          <section className={`${panelClass} p-8 text-center text-gray-500`}>
            You don't have access to Modem Recovery / Untraceable Cases.
          </section>
        ) : isSuperAdmin && !selectedEmployeeId ? (
          <section className={`${panelClass} p-8 text-center text-gray-500`}>
            Pick an employee above to view their untraceable cases.
          </section>
        ) : (
          <section className={`${panelClass} p-5 md:p-6`}>
            <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                ["Untraceable (ledger)", ledger, "text-amber-600"],
                ["Open cases", openCount, "text-gray-900"],
                ["Closed cases", closedCount, "text-gray-900"],
                ["Not yet logged", unlogged, "text-rose-600"],
              ].map(([label, value, accent]) => (
                <div
                  key={label}
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3 text-center"
                >
                  <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400">
                    {label}
                  </div>
                  <div className={`mt-1 text-lg font-semibold ${accent}`}>
                    {loading ? "…" : value}
                  </div>
                </div>
              ))}
            </div>

            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="inline-flex rounded-xl border border-gray-300 p-0.5">
                {["open", "closed", "all"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatusFilter(s)}
                    className={`rounded-lg px-4 py-1.5 text-xs font-semibold capitalize transition ${
                      statusFilter === s
                        ? "bg-amber-500 text-slate-950"
                        : "text-gray-500 hover:text-gray-800"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <div className="text-sm text-gray-500">
                {viewingLabel ? `${viewingLabel} · ` : ""}
                {loading ? "Loading…" : `${cases.length} shown`}
              </div>
              {canWrite && (
                <button
                  type="button"
                  className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400"
                  onClick={() => {
                    setDraftName("");
                    setDraftPhone("");
                    setAddOpen(true);
                  }}
                >
                  + Add Case
                </button>
              )}
            </div>

            {!cases.length && !loading && (
              <div className="rounded-2xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
                No {statusFilter === "all" ? "" : statusFilter} cases.
              </div>
            )}

            <div className="space-y-3">
              {cases.map((c) => {
                const history = sortRemarks(c);
                const latest = history[0];
                const isEditing = editingId === c._id;
                const isBusy = busyId === c._id;
                return (
                  <div
                    key={c._id}
                    className={`rounded-2xl border p-4 ${
                      c.status === "closed"
                        ? "border-gray-200 bg-gray-50/60"
                        : "border-gray-200 bg-white"
                    }`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-[220px] flex-1">
                        {isEditing ? (
                          <div className="flex flex-wrap gap-2">
                            <input
                              className={`${inputClass} max-w-[200px]`}
                              value={editDraft.customerName}
                              onChange={(e) =>
                                setEditDraft((p) => ({
                                  ...p,
                                  customerName: e.target.value,
                                }))
                              }
                              placeholder="Customer name"
                            />
                            <input
                              className={`${inputClass} max-w-[160px]`}
                              value={editDraft.phone}
                              onChange={(e) =>
                                setEditDraft((p) => ({
                                  ...p,
                                  phone: e.target.value,
                                }))
                              }
                              placeholder="Phone"
                            />
                            <button
                              type="button"
                              className="rounded-full bg-amber-500 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-950 disabled:opacity-60"
                              onClick={() => saveEdit(c)}
                              disabled={isBusy}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="rounded-full border-2 border-gray-300 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-600"
                              onClick={() => setEditingId("")}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-base font-semibold text-gray-900">
                                {c.customerName || "—"}
                              </span>
                              <span
                                className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                                  c.status === "closed"
                                    ? "bg-gray-200 text-gray-600"
                                    : "bg-amber-100 text-amber-700"
                                }`}
                              >
                                {c.status}
                              </span>
                              {canWrite && (
                                <button
                                  type="button"
                                  className="text-[11px] font-semibold text-sky-700 hover:underline"
                                  onClick={() => startEdit(c)}
                                >
                                  Edit
                                </button>
                              )}
                            </div>
                            <div className="mt-0.5 text-sm text-gray-500">
                              {c.phone || "No phone"} · added{" "}
                              {formatDateLabel(c.createdAt)}
                            </div>
                          </>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {history.length > 0 && (
                          <button
                            type="button"
                            className="rounded-full bg-amber-100 px-3 py-1 text-[11px] font-semibold text-amber-800 transition hover:bg-amber-200"
                            onClick={() => setHistoryCase(c)}
                          >
                            {history.length} follow-up
                            {history.length === 1 ? "" : "s"}
                          </button>
                        )}
                        {canWrite && c.status === "open" && (
                          <button
                            type="button"
                            className="rounded-full border-2 border-emerald-500 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-700 transition hover:bg-emerald-50 disabled:opacity-60"
                            onClick={() => handleClose(c)}
                            disabled={isBusy}
                          >
                            Close
                          </button>
                        )}
                        {canWrite && c.status === "closed" && (
                          <button
                            type="button"
                            className="rounded-full border-2 border-sky-400 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-700 transition hover:bg-sky-50 disabled:opacity-60"
                            onClick={() => handleReopen(c)}
                            disabled={isBusy}
                          >
                            Reopen
                          </button>
                        )}
                        {canWrite && (
                          <button
                            type="button"
                            className="rounded-full border-2 border-rose-300 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-rose-600 transition hover:bg-rose-50 disabled:opacity-60"
                            onClick={() => handleDelete(c)}
                            disabled={isBusy}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>

                    {latest ? (
                      <div className="mt-2 text-sm text-gray-600">
                        <span className="font-medium text-gray-700">
                          {formatDateLabel(latest.date)}
                        </span>
                        {latest.byName ? (
                          <span className="mx-1 rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-700">
                            {latest.byName}
                          </span>
                        ) : null}{" "}
                        — {latest.text || "(no remark)"}
                      </div>
                    ) : (
                      <div className="mt-2 text-sm text-gray-400">
                        No follow-ups yet.
                      </div>
                    )}

                    {c.status === "closed" && c.closeNote && (
                      <div className="mt-1 text-xs text-emerald-700">
                        Closed: {c.closeNote}
                      </div>
                    )}

                    {canWrite && c.status === "open" && (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <input
                          className={`${inputClass} min-w-[220px] flex-1`}
                          placeholder="Add a follow-up remark…"
                          value={getRemarkDraft(c._id).text}
                          onChange={(e) =>
                            setRemarkDraft(c._id, { text: e.target.value })
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleAddRemark(c);
                          }}
                        />
                        <input
                          type="date"
                          className={`${inputClass} w-40`}
                          max={toDateInputValue()}
                          value={getRemarkDraft(c._id).date}
                          onChange={(e) =>
                            e.target.value &&
                            setRemarkDraft(c._id, { date: e.target.value })
                          }
                        />
                        <button
                          type="button"
                          className="rounded-full bg-amber-500 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-950 transition hover:bg-amber-400 disabled:opacity-60"
                          onClick={() => handleAddRemark(c)}
                          disabled={
                            isBusy || !getRemarkDraft(c._id).text.trim()
                          }
                        >
                          {isBusy ? "…" : "Add follow-up"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {addOpen && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => !saving && setAddOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900">
              Add untraceable case
            </h3>
            <div className="mt-4 space-y-3">
              <div>
                <label className={labelClass}>Customer Name</label>
                <input
                  className={inputClass}
                  autoComplete="off"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  placeholder="Customer name"
                />
              </div>
              <div>
                <label className={labelClass}>Phone</label>
                <input
                  className={inputClass}
                  autoComplete="off"
                  value={draftPhone}
                  onChange={(e) => setDraftPhone(e.target.value)}
                  placeholder="Phone number"
                />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-full border-2 border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-600"
                onClick={() => setAddOpen(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleAdd}
                disabled={saving || (!draftName.trim() && !draftPhone.trim())}
              >
                {saving ? "Saving…" : "Add case"}
              </button>
            </div>
          </div>
        </div>
      )}

      {historyCase && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setHistoryCase(null)}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {historyCase.customerName || historyCase.phone || "Customer"}
                </h3>
                <p className="mt-0.5 text-sm text-gray-500">
                  {historyCase.phone ? `${historyCase.phone} · ` : ""}
                  full follow-up trail
                </p>
              </div>
              <button
                type="button"
                className="rounded-full border-2 border-gray-300 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-gray-600"
                onClick={() => setHistoryCase(null)}
              >
                Close
              </button>
            </div>
            <div className="mt-4 max-h-96 overflow-y-auto rounded-lg border border-gray-200">
              {sortRemarks(historyCase).map((h, i) => (
                <div
                  key={i}
                  className={`px-4 py-2 text-sm ${
                    i % 2 === 1 ? "bg-gray-50" : "bg-white"
                  }`}
                >
                  <span className="font-medium text-gray-700">
                    {formatDateLabel(h.date)}
                  </span>{" "}
                  {h.byName && (
                    <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-700">
                      {h.byName}
                    </span>
                  )}{" "}
                  <span className="text-gray-600">
                    — {h.text || "(no remark)"}
                  </span>
                </div>
              ))}
              {sortRemarks(historyCase).length === 0 && (
                <div className="px-4 py-3 text-sm text-gray-400">
                  No follow-ups yet.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UntraceableList;
